// RackForm - Create/Edit rack
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import dayjs, { Dayjs } from 'dayjs'
import SubHeaders from '@/components/Headers/SubHeaders'
import { paths } from '@/utils/path'
import { Button, FormItem } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getRack, createRack, updateRack, getLocations } from '../../services'
import { CreateRackRequest } from '../../types/request'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { RACK_STATUS } from '../../constants'

interface RackFormData {
    code: string
    name: string
    location_id: string
    status: string
    u_height: number
    power_supply: number
    bandwidth_limit: number
    bandwidth_reality: number
    started_at: Dayjs | null
    description: string
    notes: string
}

const STATUS_OPTIONS = Object.values(RACK_STATUS).map((s) => ({
    value: s,
    label: s.charAt(0).toUpperCase() + s.slice(1),
}))

const RackForm = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const isEditMode = Boolean(id)

    const methods = useForm<RackFormData>({
        defaultValues: {
            code: '',
            name: '',
            location_id: '',
            status: RACK_STATUS.ACTIVE,
            u_height: 42,
            power_supply: 0,
            bandwidth_limit: 0,
            bandwidth_reality: 0,
            started_at: null,
            description: '',
            notes: '',
        },
    })

    const { control, handleSubmit, reset } = methods

    // Location options for the dropdown (reuse existing locations API)
    const { data: locationsData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_LOCATIONS, 'rack-form-options'],
        queryFn: () => getLocations({ page_size: 100 }),
    })
    const locationOptions = (locationsData?.results || []).map((loc) => ({
        value: loc.id,
        label: loc.name,
    }))

    // Fetch rack data for edit mode
    const { data: rackData, isLoading } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_RACKS, id],
        queryFn: () => getRack(id!),
        enabled: isEditMode,
    })

    useEffect(() => {
        if (rackData) {
            reset({
                code: rackData.code,
                name: rackData.name,
                location_id: rackData.location_id,
                status: rackData.status,
                u_height: rackData.u_height ?? 42,
                power_supply: rackData.power_supply ?? 0,
                bandwidth_limit: rackData.bandwidth_limit ?? 0,
                bandwidth_reality: rackData.bandwidth_reality ?? 0,
                started_at: rackData.started_at
                    ? dayjs(rackData.started_at)
                    : null,
                description: rackData.description || '',
                notes: rackData.notes || '',
            })
        }
    }, [rackData, reset])

    // Create mutation
    const { mutateAsync: submitCreate, isPending: isCreating } =
        useMutationWithHandler(
            {
                mutationFn: (data: CreateRackRequest) => createRack(data),
            },
            {
                successMessage: 'Rack created successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_RACKS,
                onSuccess: () => {
                    navigate(paths.warehouseRacks)
                },
            }
        )

    // Update mutation
    const { mutateAsync: submitUpdate, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: (data: CreateRackRequest) => updateRack(id!, data),
            },
            {
                successMessage: 'Rack updated successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_RACKS,
                onSuccess: () => {
                    navigate(paths.warehouseRacks)
                },
            }
        )

    const isPending = isCreating || isUpdating

    const onSubmit = async (data: RackFormData) => {
        const payload: CreateRackRequest = {
            code: data.code,
            name: data.name,
            location_id: data.location_id,
            status: data.status,
            u_height: Number(data.u_height) || 42,
            power_supply: Number(data.power_supply) || 0,
            bandwidth_limit: Number(data.bandwidth_limit) || 0,
            bandwidth_reality: Number(data.bandwidth_reality) || 0,
            started_at: data.started_at
                ? dayjs(data.started_at).toISOString()
                : undefined,
            description: data.description || undefined,
            notes: data.notes || undefined,
        }

        if (isEditMode) {
            await submitUpdate(payload)
        } else {
            await submitCreate(payload)
        }
    }

    if (isEditMode && isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">Loading rack...</div>
            </div>
        )
    }

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    { path: paths.warehouse, title: 'Capacities & Resources' },
                    { path: paths.warehouseRacks, title: 'Racks' },
                    {
                        path: '',
                        title: isEditMode ? 'Edit Rack' : 'New Rack',
                    },
                ]}
            />

            <FormProvider {...methods}>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-2 mt-1"
                >
                    <Card>
                        <CardHeader>
                            <CardTitle>Rack Information</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormItem
                                    control={control}
                                    name="code"
                                    label="Rack Code *"
                                    type="textfield"
                                    placeholder="e.g., R-A01"
                                    rules={{
                                        required: 'Rack code is required',
                                    }}
                                />

                                <FormItem
                                    control={control}
                                    name="name"
                                    label="Rack Name *"
                                    type="textfield"
                                    placeholder="e.g., Rack A01"
                                    rules={{
                                        required: 'Rack name is required',
                                    }}
                                />

                                <FormItem
                                    control={control}
                                    name="location_id"
                                    label="Location *"
                                    type="select"
                                    options={locationOptions}
                                    placeholder="Select location..."
                                    rules={{ required: 'Location is required' }}
                                />

                                <FormItem
                                    control={control}
                                    name="status"
                                    label="Status *"
                                    type="select"
                                    options={STATUS_OPTIONS}
                                    rules={{ required: 'Status is required' }}
                                />

                                <FormItem
                                    control={control}
                                    name="u_height"
                                    label="U Height"
                                    type="number"
                                    placeholder="42"
                                />

                                <FormItem
                                    control={control}
                                    name="power_supply"
                                    label="Power Supply (W)"
                                    type="number"
                                    placeholder="0"
                                />

                                <FormItem
                                    control={control}
                                    name="bandwidth_limit"
                                    label="Bandwidth Limit (Mbps)"
                                    type="number"
                                    placeholder="0"
                                />

                                <FormItem
                                    control={control}
                                    name="bandwidth_reality"
                                    label="Bandwidth Usage (Mbps)"
                                    type="number"
                                    placeholder="0"
                                />

                                <FormItem
                                    control={control}
                                    name="started_at"
                                    label="Started At"
                                    type="datepicker"
                                />

                                <div className="md:col-span-2">
                                    <FormItem
                                        control={control}
                                        name="description"
                                        label="Description"
                                        type="textarea"
                                        placeholder="Additional description..."
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <FormItem
                                        control={control}
                                        name="notes"
                                        label="Notes"
                                        type="textarea"
                                        placeholder="Additional notes..."
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="outlined"
                            type="button"
                            onClick={() => navigate(paths.warehouseRacks)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            loading={isPending}
                        >
                            {isEditMode ? 'Update Rack' : 'Create Rack'}
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </div>
    )
}

export default RackForm
